'use client'

import { useEffect } from 'react'
import { usePostHog } from 'posthog-js/react'
import Bugsnag from '@bugsnag/js'

type ThirdPartyUser = {
  id: string
  email?: string | null
  name?: string | null
}

type ThirdPartyTraits = {
  subscriptionId?: string
  teamId?: string
  hasShop?: boolean
  partner?: string | null | undefined
  role?: string
  isGuest?: boolean
  isAdmin?: boolean
  scopeKind?: string
}

type UseThirdPartyIdentifyParams = {
  user?: ThirdPartyUser
  traits?: ThirdPartyTraits
}

export function useThirdPartyIdentify({
  user,
  traits,
}: UseThirdPartyIdentifyParams): void {
  const posthog = usePostHog()

  useEffect(() => {
    if (!user?.id || !user?.email) return
    // PostHog identification (kept active in all stages)
    if (posthog) {
      posthog.identify(user.id, {
        email: user.email,
        name: user.name,
        role: traits?.role,
        is_guest: Boolean(traits?.isGuest),
        is_admin: Boolean(traits?.isAdmin),
        has_plan: Boolean(traits?.subscriptionId),
        has_team: Boolean(traits?.teamId),
        has_shop: Boolean(traits?.hasShop),
        partner: traits?.partner ?? 'Skop',
        scope_kind: traits?.scopeKind,
      })
    }

    // Bugsnag user association (safe if not initialized)
    try {
      Bugsnag.setUser(user.id, user.email, user.name)
    } catch {}
  }, [
    posthog,
    traits?.hasShop,
    traits?.isAdmin,
    traits?.isGuest,
    traits?.partner,
    traits?.role,
    traits?.scopeKind,
    traits?.subscriptionId,
    traits?.teamId,
    user?.email,
    user?.id,
    user?.name,
  ])

  // Keep PostHog team grouping in one place
  useEffect(() => {
    if (!posthog) return
    if (traits?.teamId) {
      posthog.group('team', traits.teamId, {
        has_plan: Boolean(traits.subscriptionId),
        has_shop: Boolean(traits.hasShop),
        partner: traits.partner ?? 'Skop',
        scope_kind: traits.scopeKind ?? 'organization',
      })
    } else {
      posthog.resetGroups()
    }
  }, [
    posthog,
    traits?.hasShop,
    traits?.partner,
    traits?.scopeKind,
    traits?.subscriptionId,
    traits?.teamId,
  ])

  useEffect(() => {
    if (!posthog) return
    if (!user?.id) {
      posthog.reset()
    }
  }, [posthog, user?.id])
}
